import type { ArchiveEntry, MarketSnapshot, Report, SystemStatus } from "../types";
import type { CursorPage, EstimateHistoryProjection, EstimateRevision, MarketCard, PageInfo, PublicationManifest, PublicPageRequest, RecentIntelligenceEntry, PublicProjections } from "../publication/types";
import type { FtmDataProvider } from "./ftmDataProvider";

type FetchLike = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

export interface CloudPublicSnapshot extends PublicProjections {
  marketSnapshot?: MarketSnapshot;
  systemStatus?: SystemStatus;
}
interface CloudProviderOptions {
  basePath?: string;
  fetcher?: FetchLike;
}

async function loadSnapshot(basePath: string, fetcher: FetchLike): Promise<CloudPublicSnapshot> {
  const response = await fetcher(`${basePath}/snapshot`, { cache: "no-store", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`Cloud public snapshot returned ${response.status}.`);
  let payload: unknown;
  try { payload = await response.json(); } catch { throw new Error("Cloud public snapshot returned invalid JSON."); }
  if (!payload || typeof payload !== "object" || !(payload as { ok?: boolean }).ok || !(payload as { snapshot?: unknown }).snapshot) throw new Error("Cloud public snapshot is malformed.");
  const snapshot = (payload as { snapshot: unknown }).snapshot;
  if (!snapshot || typeof snapshot !== "object" || !Array.isArray((snapshot as PublicProjections).reports) || !(snapshot as PublicProjections).manifest) throw new Error("Cloud public snapshot schema is invalid.");
  return snapshot as CloudPublicSnapshot;
}

function pageQuery(request: PublicPageRequest = {}): string {
  const params = new URLSearchParams();
  if (request.limit !== undefined) params.set("limit", String(request.limit));
  if (request.cursor) params.set("cursor", request.cursor);
  if (request.category) params.set("category", request.category);
  if (request.search) params.set("search", request.search);
  const query = params.toString();
  return query ? `?${query}` : "";
}

function validPageInfo(value: unknown): value is PageInfo {
  if (!value || typeof value !== "object") return false;
  const page = value as Record<string, unknown>;
  return typeof page.limit === "number" && Number.isInteger(page.limit) && typeof page.hasMore === "boolean" && (typeof page.nextCursor === "string" || page.nextCursor === null);
}

async function loadPage<T>(basePath: string, path: string, key: "reports" | "entries", request: PublicPageRequest, fetcher: FetchLike): Promise<CursorPage<T>> {
  const response = await fetcher(`${basePath}/${path}${pageQuery(request)}`, { cache: "no-store", headers: { Accept: "application/json" } });
  if (!response.ok) throw new Error(`Cloud public ${path} returned ${response.status}.`);
  let payload: unknown;
  try { payload = await response.json(); } catch { throw new Error(`Cloud public ${path} returned invalid JSON.`); }
  if (!payload || typeof payload !== "object" || !(payload as { ok?: boolean }).ok || !Array.isArray((payload as Record<string, unknown>)[key]) || !validPageInfo((payload as Record<string, unknown>).page)) throw new Error(`Cloud public ${path} page schema is invalid.`);
  return { items: (payload as Record<string, unknown>)[key] as T[], page: (payload as Record<string, unknown>).page as PageInfo };
}

export class CloudFtmDataProvider implements FtmDataProvider {
  private readonly basePath: string;
  private readonly fetcher: FetchLike;

  constructor(options: CloudProviderOptions = {}) {
    this.basePath = (options.basePath ?? "/api/public").replace(/\/$/, "");
    this.fetcher = options.fetcher ?? ((input, init) => globalThis.fetch(input, init));
  }

  private snapshot(): Promise<CloudPublicSnapshot> { return loadSnapshot(this.basePath, this.fetcher); }

  async getMarketSnapshot(): Promise<MarketSnapshot> {
    const snapshot = await this.snapshot();
    return snapshot.marketSnapshot ?? { markets: [], capturedAt: null };
  }

  async getCurrentReports(): Promise<Report[]> { return (await this.snapshot()).reports; }

  async getCurrentReportsPage(request: PublicPageRequest = {}): Promise<CursorPage<Report>> { return loadPage<Report>(this.basePath, "reports", "reports", request, this.fetcher); }

  async getReport(slug: string): Promise<Report | null> {
    const response = await this.fetcher(`${this.basePath}/reports/${encodeURIComponent(slug)}`, { cache: "no-store", headers: { Accept: "application/json" } });
    if (!response.ok) throw new Error(`Cloud public report detail returned ${response.status}.`);
    let payload: unknown;
    try { payload = await response.json(); } catch { throw new Error("Cloud public report detail returned invalid JSON."); }
    if (!payload || typeof payload !== "object" || !(payload as { ok?: boolean }).ok || !Array.isArray((payload as Record<string, unknown>).reports)) throw new Error("Cloud public report detail schema is invalid.");
    return ((payload as { reports: Report[] }).reports[0] ?? null);
  }

  async getArchive(): Promise<ArchiveEntry[]> { return (await this.snapshot()).archive; }

  async getArchivePage(request: PublicPageRequest = {}): Promise<CursorPage<ArchiveEntry>> { return loadPage<ArchiveEntry>(this.basePath, "archive", "entries", request, this.fetcher); }

  async getSystemStatus(): Promise<SystemStatus> { return (await this.snapshot()).systemStatus ?? { content_source: "d1" }; }

  async getRecentIntelligence(): Promise<RecentIntelligenceEntry[]> { return (await this.snapshot()).recentIntelligence; }
  async getMarketCards(): Promise<MarketCard[]> { return (await this.snapshot()).marketCards; }
  async getEstimateHistories(): Promise<EstimateHistoryProjection[]> { return (await this.snapshot()).estimateHistories; }
  async getEstimateRevisions(): Promise<EstimateRevision[]> { return (await this.snapshot()).estimateRevisions; }
  async getPublicationManifest(): Promise<PublicationManifest | null> { return (await this.snapshot()).manifest; }
}

export function createCloudFtmDataProvider(options: CloudProviderOptions = {}): CloudFtmDataProvider {
  return new CloudFtmDataProvider(options);
}
